import React, { useState, useEffect } from 'react';
import ResourceHeader from './resource/ResourceHeader';
import BasicInformation from './resource/BasicInformation';
import PermissionRoleSection from './resource/PermissionRoleSection';
import CompensationSection from './resource/CompensationSection';
import AvailabilitySection from './resource/AvailabilitySection';
import QuickActions from './resource/QuickActions';
import { Resource, updateResource, activateResource, deleteResource } from '../lib/resources';
import { useBusiness } from '../contexts/BusinessContext';

interface ResourceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  resource: Resource | null;
  onUpdate: () => void;
}

export default function ResourceDetailsModal({ isOpen, onClose, resource, onUpdate }: ResourceDetailsModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState<{ info?: boolean; pricing?: boolean }>({});
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Resource>>({});
  const [localResource, setLocalResource] = useState<Resource | null>(null);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [activateLoading, setActivateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editMode, setEditMode] = useState<string | null>(null);

  // Function to completely reset all state
  const resetAllState = () => {
    console.log("Completely resetting resource modal state");
    setFormData({});
    setLocalResource(null);
    setShowDeleteConfirm(false);
    setEditMode(null);
    setError(null);
    setLoading({});
    setDeactivateLoading(false);
    setActivateLoading(false);
    setDeleteLoading(false);
  };

  useEffect(() => {
    if (isOpen && resource) {
      console.log(`Opening resource modal for: ${resource.id} - ${resource.first_name} ${resource.last_name}`);
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
      
      // Initialize form data with both commission fields synchronized
      const initialFormData = {
        ...resource,
        // Ensure both commission fields are set if either exists
        commission_percentage: resource.commission_percentage || resource.commission_rate || 0,
        commission_rate: resource.commission_rate || resource.commission_percentage || 0
      };
      
      setFormData(initialFormData);
      setLocalResource(resource);
      setShowDeleteConfirm(false);
    } else {
      console.log("Closing resource modal, cleaning up state");
      document.body.style.overflow = 'unset';
      
      // When modal is being closed, completely reset state
      if (!isOpen) {
        resetAllState();
      }
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, resource]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleSave = async (section: string) => {
    if (!resource) return;

    try {
      setLoading({ info: true, pricing: true });
      setError(null);

      // Filter out fields that we've hidden and shouldn't be updated
      const { role, system_phone_number, permission_role, ...cleanFormData } = formData;
      
      // Ensure numeric fields are properly converted
      if (cleanFormData.hourly_rate !== undefined) {
        cleanFormData.hourly_rate = Number(cleanFormData.hourly_rate) || 0;
      }
      if (cleanFormData.flat_rate_amount !== undefined) {
        cleanFormData.flat_rate_amount = Number(cleanFormData.flat_rate_amount) || 0;
      }
      
      // Handle commission compensation - ensure both commission_rate and commission_percentage are set and are numbers
      if (cleanFormData.compensation_type === 'commission') {
        const commissionValue = Number(cleanFormData.commission_percentage) || 0;
        cleanFormData.commission_percentage = commissionValue;
        cleanFormData.commission_rate = commissionValue;
      }
      
      // Validate commission percentage
      if (cleanFormData.compensation_type === 'commission' && cleanFormData.commission_percentage !== undefined && 
          (cleanFormData.commission_percentage < 0 || cleanFormData.commission_percentage > 100)) {
        throw new Error('Commission percentage must be between 0 and 100');
      }
      
      // Ensure required fields are properly set to avoid constraint violations
      if (cleanFormData.is_mobile_available === undefined || cleanFormData.is_mobile_available === null) {
        cleanFormData.is_mobile_available = false;
      }
      
      // Ensure compensation_type is valid
      if (!cleanFormData.compensation_type || !['hourly', 'flat_rate', 'commission'].includes(cleanFormData.compensation_type)) {
        cleanFormData.compensation_type = 'hourly';
      }
      
      console.log('=== RESOURCE SAVE DEBUG ===');
      console.log('Original resource:', JSON.stringify(resource, null, 2));
      console.log('Form data before clean:', JSON.stringify(formData, null, 2));
      console.log('Clean data being sent:', JSON.stringify(cleanFormData, null, 2));
      console.log('Compensation type check:', cleanFormData.compensation_type);
      console.log('Mobile available check:', cleanFormData.is_mobile_available);
      console.log('=== END DEBUG ===');

      const updatedResource = await updateResource(resource.id, cleanFormData);
      
      setLocalResource(updatedResource);
      setEditMode(null);
      
      onUpdate();
    } catch (err) {
      console.error('=== ERROR DETAILS ===');
      console.error('Full error object:', err);
      console.error('Error message:', err instanceof Error ? err.message : 'Unknown error');
      console.error('Error stack:', err instanceof Error ? err.stack : 'No stack trace');
      console.error('FormData that failed:', JSON.stringify(formData, null, 2));
      
      // Try to extract more details from the error
      if (err && typeof err === 'object') {
        console.error('Error details:', JSON.stringify(err, null, 2));
        if ('details' in err) {
          console.error('Error details property:', err.details);
        }
        if ('hint' in err) {
          console.error('Error hint:', err.hint);
        }
        if ('code' in err) {
          console.error('Error code:', err.code);
        }
      }
      console.error('=== END ERROR DETAILS ===');
      
      // Provide more specific error messages
      let errorMessage = 'Failed to update team member';
      if (err instanceof Error) {
        console.log('Full error message:', err.message);
        
        if (err.message.includes('permission')) {
          errorMessage = 'Permission denied: You do not have access to update this team member';
        } else if (err.message.includes('check constraint')) {
          if (err.message.includes('compensation_type')) {
            errorMessage = 'Invalid compensation type. Must be hourly, flat_rate, or commission.';
          } else if (err.message.includes('is_mobile_available')) {
            errorMessage = 'Mobile availability setting is required.';
          } else {
            errorMessage = `Database validation error: ${err.message}`;
          }
        } else if (err.message.includes('constraint') || err.message.includes('violates')) {
          errorMessage = `Database constraint violation: ${err.message}`;
        } else if (err.message.includes('network') || err.message.includes('fetch')) {
          errorMessage = 'Network error: Please check your connection and try again';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading({ info: false, pricing: false });
    }
  };

  const handleDeactivate = async () => {
    if (!resource) return;

    try {
      setDeactivateLoading(true);
      setError(null);

      const updatedResource = await updateResource(resource.id, {
        status: 'inactive'
      });
      
      setLocalResource(updatedResource);
      onUpdate();
    } catch (err) {
      console.error('Error deactivating resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to deactivate team member');
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!resource) return;

    try {
      setActivateLoading(true);
      setError(null);

      // Don't use activateResource here as it only changes the status
      // Instead use updateResource to update the status
      const updatedResource = await updateResource(resource.id, {
        status: 'active'
      });
      
      setLocalResource(updatedResource);
      onUpdate();
    } catch (err) {
      console.error('Error activating resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to activate team member');
    } finally {
      setActivateLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!resource) return;
    
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }
    
    try {
      setDeleteLoading(true);
      setError(null);
      
      await deleteResource(resource.id);
      
      onUpdate();
      handleClose();
    } catch (err) {
      console.error('Error deleting resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete team member');
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  // Add a custom close handler that ensures cleanup
  const handleClose = () => {
    console.log("Resource modal close handler called");
    // Reset state before closing
    resetAllState();
    // Call the provided onClose function
    onClose();
  };

  if (!isOpen && !isAnimating) return null;
  if (!localResource) return null;

  return (
    <div 
      className={`fixed inset-0 z-[9999] transition-opacity duration-300 resource-details-modal ${
        isOpen ? 'opacity-100' : 'opacity-0'
      }`}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`fixed inset-0 bg-white transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="h-screen flex flex-col">
          <ResourceHeader resource={localResource} onClose={handleClose} />

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-[#f6f6f7]">
            <div className="max-w-5xl mx-auto py-6 px-6">
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              {showDeleteConfirm && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                  <p className="text-sm mb-2">Are you sure you want to permanently delete this team member? This action cannot be undone.</p>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleDelete}
                      disabled={deleteLoading}
                      className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                    >
                      {deleteLoading ? 'Deleting...' : 'Confirm Delete'}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="px-3 py-1 bg-gray-200 text-gray-800 text-xs rounded hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Modified grid layout with responsive classes */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Left Column - will occupy full width on mobile, 2/3 width on desktop */}
                <div className="md:col-span-2 space-y-6 order-1">
                  <BasicInformation
                    resource={localResource}
                    editMode={editMode}
                    formData={formData}
                    setFormData={setFormData}
                    onSave={handleSave}
                    onEdit={setEditMode}
                    loading={loading.info}
                  />

                  <PermissionRoleSection
                    resource={localResource}
                    editMode={editMode}
                    onEdit={setEditMode}
                    onUpdate={onUpdate}
                  />

                  <CompensationSection
                    resource={localResource}
                    editMode={editMode}
                    formData={formData}
                    setFormData={setFormData}
                    onSave={handleSave}
                    onEdit={setEditMode}
                    loading={loading.info}
                  />

                  <AvailabilitySection
                    resource={localResource}
                    editMode={editMode}
                    formData={formData}
                    setFormData={setFormData}
                    onSave={handleSave}
                    onEdit={setEditMode}
                    loading={loading.info}
                  />
                </div>

                {/* Quick Actions - moved below on mobile, right column on desktop */}
                <div className="order-2 space-y-6">
                  <QuickActions
                    resource={localResource}
                    onDeactivate={handleDeactivate}
                    onActivate={handleActivate}
                    onDelete={handleDelete}
                    deactivateLoading={deactivateLoading}
                    activateLoading={activateLoading}
                    deleteLoading={deleteLoading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}